import { AOAlgorithm } from "./threejs/higharc/AOAlgorithm";
import type { GTAOPassParams } from "./threejs/higharc/GTAOPass";
import type { SoftShadowParams } from "./threejs/higharc/MaterialSharedDefines";
import { RenderParamsSource } from "./threejs/higharc/RenderParamsSource";
import type { SAOPass2Params } from "./threejs/higharc/SAOPass2";
import type { ScreenRenderParams } from "./threejs/higharc/SSFXRendererParams";
import type { RenderPipelineParams } from "./threejs/higharc/SSFXRendererTypes";

export interface PerformanceTier {
  id: string; // This should be used to identify performance tiers if user preference is serialized somewhere, for example.
  iPhoneOrAndroid: boolean;
  minScore: number;
  maxScore: number;
  name: string;

  params: PerformanceTierParameters;
  useSimplifiedMaterials: boolean;
}

type PerformanceTierScreenParams = Pick<
  ScreenRenderParams,
  "ssaaEnabled" | "renderResolutionMultiplier" | "useLightweightPipeline"
>;
type PerformanceTierRenderPipelineParams = Pick<
  RenderPipelineParams,
  "aoAlgorithm" | "screenSpaceShadowsEnabled" | "smallScaleAOEnabled"
>;
type PerformanceTierSAOParams = Pick<SAOPass2Params, "aoResolutionMultiplier">;
type PerformanceTierGTAOParams = Pick<GTAOPassParams, "aoResolutionMultiplier" | "usePoissonBlur">;
type PerformanceTierShadowParams = Pick<SoftShadowParams, "pcssPenumbraAffectedByDistance">;

interface PerformanceTierParameters {
  screenRenderParams: PerformanceTierScreenParams;
  renderPipelineParams: PerformanceTierRenderPipelineParams;
  saoParams: PerformanceTierSAOParams;
  gtaoParams: PerformanceTierGTAOParams;
  id: number;
  softShadowParams: PerformanceTierShadowParams;
  source: RenderParamsSource;
}

/**
 * Named so offscreen renderers can pin a tier instead of resolving one from `benchmarkGpu`, whose
 * score varies by host. Identical to HIGH, so it is the top of the non-mobile range.
 */
export const MEDIUM_PERFORMANCE_TIER: PerformanceTier = {
  id: "MEDIUM",
  iPhoneOrAndroid: false,
  minScore: 60,
  maxScore: 120,
  name: "Medium",

  params: {
    screenRenderParams: {
      ssaaEnabled: true,
      renderResolutionMultiplier: 1.0,
      useLightweightPipeline: false,
    },
    renderPipelineParams: {
      aoAlgorithm: AOAlgorithm.GTAO,
      screenSpaceShadowsEnabled: true,
      smallScaleAOEnabled: true,
    },
    saoParams: {
      aoResolutionMultiplier: 0.5,
    },
    gtaoParams: {
      aoResolutionMultiplier: 0.5,
      usePoissonBlur: true,
    },
    softShadowParams: {
      pcssPenumbraAffectedByDistance: true,
    },
    id: 2,
    source: RenderParamsSource.PERFORMANCE_TIER,
  },
  useSimplifiedMaterials: false,
};

export const PERFORMANCE_TIERS: PerformanceTier[] = [
  {
    id: "MOBILE",
    iPhoneOrAndroid: true,
    minScore: -Infinity,
    maxScore: Infinity,
    name: "Mobile",

    params: {
      screenRenderParams: {
        ssaaEnabled: false,
        renderResolutionMultiplier: 1.0,
        useLightweightPipeline: true,
      },
      renderPipelineParams: {
        aoAlgorithm: AOAlgorithm.SAO,
        screenSpaceShadowsEnabled: false,
        smallScaleAOEnabled: false,
      },
      saoParams: {
        aoResolutionMultiplier: 0.4,
      },
      gtaoParams: {
        aoResolutionMultiplier: 0.25,
        usePoissonBlur: true,
      },
      softShadowParams: {
        pcssPenumbraAffectedByDistance: false,
      },
      id: 0,
      source: RenderParamsSource.PERFORMANCE_TIER,
    },
    useSimplifiedMaterials: true,
  },
  {
    id: "LOW",
    iPhoneOrAndroid: false,
    minScore: -Infinity,
    maxScore: 60,
    name: "Low",

    params: {
      screenRenderParams: {
        ssaaEnabled: true,
        renderResolutionMultiplier: 1.0,
        useLightweightPipeline: false,
      },
      renderPipelineParams: {
        aoAlgorithm: AOAlgorithm.GTAO,
        screenSpaceShadowsEnabled: false,
        smallScaleAOEnabled: false,
      },
      saoParams: {
        aoResolutionMultiplier: 0.5,
      },
      gtaoParams: {
        aoResolutionMultiplier: 0.25,
        usePoissonBlur: false,
      },
      softShadowParams: {
        pcssPenumbraAffectedByDistance: true,
      },
      id: 1,
      source: RenderParamsSource.PERFORMANCE_TIER,
    },
    useSimplifiedMaterials: false,
  },
  MEDIUM_PERFORMANCE_TIER,
  {
    id: "HIGH",
    iPhoneOrAndroid: false,
    minScore: 120,
    maxScore: Infinity,
    name: "High",

    params: {
      screenRenderParams: {
        ssaaEnabled: true,
        renderResolutionMultiplier: 1.0,
        useLightweightPipeline: false,
      },
      renderPipelineParams: {
        aoAlgorithm: AOAlgorithm.GTAO,
        screenSpaceShadowsEnabled: true,
        smallScaleAOEnabled: true,
      },
      saoParams: {
        aoResolutionMultiplier: 0.5,
      },
      gtaoParams: {
        aoResolutionMultiplier: 0.5,
        usePoissonBlur: true,
      },
      softShadowParams: {
        pcssPenumbraAffectedByDistance: true,
      },
      id: 3,
      source: RenderParamsSource.PERFORMANCE_TIER,
    },
    useSimplifiedMaterials: false,
  },
];

export function getPerformanceTierByScore(
  performanceScore: number /** gpuInfo FPS or fallback value */,
  iPhoneOrAndroidDetected: boolean,
): PerformanceTier {
  for (const tier of PERFORMANCE_TIERS) {
    if (
      iPhoneOrAndroidDetected === tier.iPhoneOrAndroid &&
      performanceScore >= tier.minScore &&
      performanceScore < tier.maxScore
    ) {
      return tier;
    }
  }
  return PERFORMANCE_TIERS[0];
}
